import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, Subject, catchError, map, throwError } from 'rxjs';
import { PlaceAutoCompleteEntry } from 'src/app/core/models/place-auto-complete-entry';
import { PlaceDetail } from 'src/app/core/models/place-detail';
import { EnvService } from 'src/app/core/services/env/env.service';

declare var google: any;

@Injectable({
  providedIn: 'root'
})
export class PlacesService {

  private defaultMapAttribution: any = null;

  constructor(
    private envService: EnvService,
    private http: HttpClient) {

  }

  setDefaultMapAttribution(mapAttribution: any) {
    this.defaultMapAttribution = mapAttribution;
  }

  createSession(): any {
    console.log('PlacesService.createSession');
    return new google.maps.places.AutocompleteSessionToken();
  }

  searchLocationForAutoComplete(input: string, token: any, types?: string[]): Observable<PlaceAutoCompleteEntry[]> {


    const subject = new Subject<PlaceAutoCompleteEntry[]>();

    const service = new google.maps.places.AutocompleteService();
    const request = types ? { input, types, sessionToken: token }
      : { input, sessionToken: token };


    service.getPlacePredictions(request, (predictions: any[], status: string) => {
      if (status === 'OK') {
        const entries = predictions.map((item: { place_id: any; id: any; types: string | string[]; structured_formatting: { main_text: any; secondary_text: any; } | null; }) => {
          const placesAutoCompleteEntry = new PlaceAutoCompleteEntry();
          placesAutoCompleteEntry.placeId = item.place_id;
          placesAutoCompleteEntry.description = item.id;

          const isCountry = item.types ? item.types.includes('country') : false;
          if (item.structured_formatting != null) {
            placesAutoCompleteEntry.primaryText = item.structured_formatting.main_text;
            placesAutoCompleteEntry.secondaryText = item.structured_formatting.secondary_text
              ? item.structured_formatting.secondary_text
              : (isCountry ? item.structured_formatting.main_text : null);
          }

          placesAutoCompleteEntry.icon = findIconForType(item.types);
          return placesAutoCompleteEntry;
        });

        subject.next(entries);
        subject.complete();
      }
    });

    return subject;
  }



  fetchAddresseDetails(placeId: string, element?: any): Promise<PlaceDetail> {


    const allFields = [
      'address_components',
      'formatted_address',
      'name',
      'url',
      'geometry',
      'types',
    ];

    return new Promise((resolve, reject) => {
      const request = {
        placeId,
        fields: allFields
      };

      element = element == null ? this.defaultMapAttribution : element;

      const service = new google.maps.places.PlacesService(element);


      service.getDetails(request, (place: any, status: any) => {
        if (status === google.maps.places.PlacesServiceStatus.OK) {
          const placeDetail = this.buildPlaceDetail(place);
          placeDetail.placeId = placeId;
          placeDetail.externalId = placeId;
          resolve(placeDetail);
        }
      });
    });
  }

  buildPlaceDetail(place: { place_id: any; name: any; formatted_address: any; international_phone_number: any; rating: any; user_ratings_total: any; website: any; url: any; types: string[]; geometry: { location: { lat: () => any; lng: () => any; }; }; utc_offset_minutes: any; address_components: any[]; }): PlaceDetail {
    const placeDetail = new PlaceDetail();
    placeDetail.placeId = place.place_id;
    placeDetail.name = place.name;
    placeDetail.formattedAddress = place.formatted_address;
    placeDetail.phoneNumber = place.international_phone_number;
    placeDetail.rating = place.rating;
    placeDetail.ratingCount = place.user_ratings_total;
    placeDetail.websiteUrl = place.website;
    placeDetail.mapUrl = place.url;
    placeDetail.placeType = findType(place.types);
    placeDetail.icon = findIconForType(placeDetail.placeType);
    placeDetail.latitude = place.geometry.location.lat();
    placeDetail.longitude = place.geometry.location.lng();
    placeDetail.utcOffsetMinutes = place.utc_offset_minutes;
    placeDetail.city = place.address_components
      .find((component: { types: any[]; }) => component.types
        .find((type: string) => type === 'locality')) != null
      ? place.address_components.find((component: { types: any[]; }) => component.types
        .find((type: string) => type === 'locality')).long_name : '';
    placeDetail.district = place.address_components.find((component: { types: any[]; }) => component.types
      .find((type: string) => type === 'administrative_area_level_2')) != null
      ? place.address_components
        .find((component: { types: any[]; }) => component.types
          .find((type: string) => type === 'administrative_area_level_2')).long_name : '';
    placeDetail.state = place.address_components.find((component: { types: any[]; }) => component.types
      .find((type: string) => type === 'administrative_area_level_1')) != null
      ? place.address_components
        .find((component: { types: any[]; }) => component.types
          .find((type: string) => type === 'administrative_area_level_1')).long_name : '';
    placeDetail.countryCode = place.address_components
      .find((component: { types: any[]; }) => component.types
        .find((type: string) => type === 'country')) != null
      ? place.address_components
        .find((component: { types: any[]; }) => component.types
          .find((type: string) => type === 'country')).short_name : '';
    placeDetail.countryName = place.address_components
      .find((component: { types: any[]; }) => component.types
        .find((type: string) => type === 'country')) != null
      ? place.address_components
        .find((component: { types: any[]; }) => component.types
          .find((type: string) => type === 'country')).long_name : '';
    return placeDetail;
  }

  fetchPlaceDetails(placeId: string, element?: any): Promise<PlaceDetail> {

    const allFields = [
      'address_components',
      'formatted_address',
      'international_phone_number',
      'name',
      'rating',
      'url',
      'user_ratings_total',
      'geometry',
      'types',
      'website',
      'utc_offset_minutes'
    ];

    return new Promise((resolve, reject) => {
      const request = {
        placeId,
        fields: allFields
      };

      element = element == null ? this.defaultMapAttribution : element;

      const service = new google.maps.places.PlacesService(element);
      service.getDetails(request, (place: { name: any; formatted_address: any; international_phone_number: any; rating: any; user_ratings_total: any; website: any; url: any; types: string[]; geometry: { location: { lat: () => any; lng: () => any; }; }; utc_offset_minutes: any; address_components: any[]; }, status: any) => {
        if (status === google.maps.places.PlacesServiceStatus.OK) {
          const placeDetail = new PlaceDetail();
          placeDetail.placeId = placeId;
          placeDetail.externalId = placeId;
          placeDetail.name = place.name;
          placeDetail.formattedAddress = place.formatted_address;
          placeDetail.phoneNumber = place.international_phone_number;
          placeDetail.rating = place.rating;
          placeDetail.ratingCount = place.user_ratings_total;
          placeDetail.websiteUrl = place.website;
          placeDetail.mapUrl = place.url;
          placeDetail.placeType = findType(place.types);
          placeDetail.icon = findIconForType(placeDetail.placeType);
          placeDetail.latitude = place.geometry.location.lat();
          placeDetail.longitude = place.geometry.location.lng();
          placeDetail.utcOffsetMinutes = place.utc_offset_minutes;
          placeDetail.city = place.address_components
            .find((component: { types: any[]; }) => component.types
              .find((type: string) => type === 'locality')) != null
            ? place.address_components.find((component: { types: any[]; }) => component.types
              .find((type: string) => type === 'locality')).long_name : '';
          placeDetail.district = place.address_components.find((component: { types: any[]; }) => component.types
            .find((type: string) => type === 'administrative_area_level_2')) != null
            ? place.address_components
              .find((component: { types: any[]; }) => component.types
                .find((type: string) => type === 'administrative_area_level_2')).long_name : '';
          placeDetail.state = place.address_components.find((component: { types: any[]; }) => component.types
            .find((type: string) => type === 'administrative_area_level_1')) != null
            ? place.address_components
              .find((component: { types: any[]; }) => component.types
                .find((type: string) => type === 'administrative_area_level_1')).long_name : '';
          placeDetail.countryCode = place.address_components
            .find((component: { types: any[]; }) => component.types
              .find((type: string) => type === 'country')) != null
            ? place.address_components
              .find((component: { types: any[]; }) => component.types
                .find((type: string) => type === 'country')).short_name : '';
          placeDetail.countryName = place.address_components
            .find((component: { types: any[]; }) => component.types
              .find((type: string) => type === 'country')) != null
            ? place.address_components
              .find((component: { types: any[]; }) => component.types
                .find((type: string) => type === 'country')).long_name : '';
          resolve(placeDetail);
        }
      });
    });
  }

  fetchPlaceTypes(): Promise<Array<{ type: string, description: string }>> {
    return new Promise((resolve, reject) => {
      resolve(placeTypes);
    });
  }

  fetchNearBy(element: any, type: string | any[] | null, searchRadius: number, latitude: any, longitude: any): Promise<PlaceDetail[]> {

    return new Promise((resolve, reject) => {
      const service = new google.maps.places.PlacesService(element);
      const circleOptions = {
        center: { lat: latitude, lng: longitude },
        radius: searchRadius * 1000
      };

      type = type == null || type.length == 0 ? placeTypeIds : type;

      const request = {
        bounds: new google.maps.Circle(circleOptions).getBounds(),
        type
      };

      const iteration = { count: 1 };

      const response: any[] | PromiseLike<PlaceDetail[]> = [];

      service.nearbySearch(request, (placeResults: null, status: any, pagination: { hasNextPage: any; nextPage: () => void; }) => {
        const results = placeResults == null ? [] : placeResults;
        results
          .filter((item: { types: string[]; }) => contains(item.types, placeTypeIds))
          .map((place: { place_id: any; name: any; formatted_address: null; vicinity: any; international_phone_number: any; rating: any; user_ratings_total: any; website: any; url: any; types: string[]; geometry: { location: { lat: () => any; lng: () => any; }; }; }) => {
            const placeDetail = new PlaceDetail();
            placeDetail.externalId = place.place_id;
            placeDetail.name = place.name;
            placeDetail.formattedAddress = place.formatted_address != null ? place.formatted_address : place.vicinity;
            placeDetail.phoneNumber = place.international_phone_number;
            placeDetail.rating = place.rating;
            placeDetail.ratingCount = place.user_ratings_total;
            placeDetail.websiteUrl = place.website;
            placeDetail.mapUrl = place.url;
            placeDetail.placeType = findType(place.types);
            placeDetail.icon = findIconForType(placeDetail.placeType);
            placeDetail.latitude = place.geometry.location.lat();
            placeDetail.longitude = place.geometry.location.lng();
            return placeDetail;
          }).forEach((item: any) => response.push(item));

        if (!pagination.hasNextPage || iteration.count > 5 || response.length > 100) {
          resolve(response);
        } else {
          iteration.count++;
          pagination.nextPage();
        }

      });

    });
  }


  getPlaceDetailsFromCache(placeId: string): Observable<PlaceDetail> {
    const serviceUrl = this.envService.getUrlForProvider('core').featureUrl('place');
    return this.http.get<PlaceDetail>(`${serviceUrl}/${placeId}`).pipe(
      catchError((error: any) => throwError(error)),
    );

  }

  savePlaceDetailsInCache(placeDetail: PlaceDetail) {
    const serviceUrl = this.envService.getUrlForProvider('core').featureUrl('place');
    return this.http.post<PlaceDetail>(`${serviceUrl}`, placeDetail, httpOptions).pipe(
      catchError((error: any) => throwError(error)),
    );
  }

  updatePlaceDetailsInCache(placeId: string, placeDetail: PlaceDetail) {
    if (placeId == null) {
      return this.savePlaceDetailsInCache(placeDetail);
    } else {
      const serviceUrl = this.envService.buildFeatureUrl('core', 'updatePlaceCache', [placeId]);
      return this.http.put<PlaceDetail>(`${serviceUrl}`, placeDetail, httpOptions).pipe(
        catchError((error: any) => throwError(error)),
      );
    }
  }

  distanceBetweenPlacesInKms(first: PlaceDetail, second: PlaceDetail): number {
    if (first != null && second != null) {

      return this.distanceInKms(
        first.latitude!,
        first.longitude!,
        second.latitude!,
        second.longitude!);
    }
    return 0;
  }

  distanceInKms(lat1: number, lon1: number, lat2: number, lon2: number): number {

    const deg2rad = (deg: number) => deg * (Math.PI / 180);
    const R = 6371; // Radius of the earth in km
    var dLat = deg2rad(lat2 - lat1);  // deg2rad below
    var dLon = deg2rad(lon2 - lon1);
    var a = Math.sin(dLat / 2) * Math.sin(dLat / 2)
      + Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
    var c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    var d = R * c; // Distance in km
    return d;
  }
}

const httpOptions = {
  headers: new HttpHeaders({ 'Content-Type': 'application/json' })
};

const contains = (list: string[], types: string[]): boolean => {

  // tslint:disable-next-line: forin
  for (const item of list) {
    if (types.indexOf(item) > -1) {
      return true;
    }
  }
  return false;
};



const capitalize = (str: string) => {
  return (str ? str.toLowerCase() : str).replace(/(?:^|\s)\S/g, (ch: string) => ch.toUpperCase());
};

const description = (type: string) => {
  return type.split('_')
    .map(word => capitalize(word))
    .join(' ');
};

const placeTypeGroups = [
  {
    id: 'laundry',
    icon: 'mdi-washing-machine',
    text: 'Laundry',
    categories: ['laundry']
  },
  {
    id: 'stations',
    icon: 'mdi-train',
    text: 'Local Transport',
    categories: [
      'train_station',
      'light_rail_station',
      'subway_station',
      'car_rental',
      'bus_station',
      'taxi_stand']
  },
  {
    id: 'airports',
    icon: 'mdi-airport',
    text: 'Airports',
    categories: ['airport']
  },
  {
    id: 'gym',
    icon: 'mdi-weight-lifter',
    text: 'Gym',
    categories: ['gym']
  },
  {
    id: 'parks',
    icon: 'mdi-beach',
    text: 'Bleasure',
    categories: [
      'bowling_alley',
      'casino',
      'amusement_park',
      'tourist_attraction',
      'zoo',
      'art_gallery',
      'movie_theater',
      'museum',
      'night_club']
  },
  {
    id: 'places-of-worship',
    icon: 'mdi-beach',
    text: 'Places of Worship',
    categories: [
      'church',
      'hindu_temple',
      'mosque'
    ]
  },
  {
    id: 'food',
    icon: 'mdi-silverware',
    text: 'Food & Beverages',
    categories: [
      'bakery',
      'bar',
      'restaurant',
      'cafe',
      'meal_delivery',
      'meal_takeaway'
    ]
  },
  {
    id: 'bank',
    icon: 'mdi-cash-multiple',
    text: 'Banks & ATMs',
    categories: [
      'bank',
      'atm'
    ]
  },
  {
    id: 'salon',
    icon: 'mdi-hair-dryer',
    text: 'Salons & Spas',
    categories: [
      'beauty_salon',
      'hair_care',
      'spa',
      'subway_station'
    ]
  },
  {
    id: 'medical',
    icon: 'mdi-hospital-box',
    text: 'Medical Support',
    categories: [
      'dentist',
      'doctor',
      'drugstore',
      'hospital',
      'pharmacy',
      'health'
    ]
  },
  {
    id: 'shopping',
    icon: 'mdi-cart',
    text: 'Shopping',
    categories: [
      'supermarket',
      'clothing_store',
      'liquor_store',
      'shopping_mall',
      'convenience_store',
      'jewelry_store',
      'department_store',
      'grocery_or_supermarket',
      'home_goods_store']
  }
];

const placeTypeIds: string[] = placeTypeGroups
  .reduce<string[]>((acc, item) => [...acc, ...item.categories], []);

const placeTypes = placeTypeIds.map(type => ({
  type,
  description: description(type)
}));

const findType = (types: string[]) => {

  const group = placeTypeGroups
    .find(group => contains(group.categories, types))

  return group != null ? group.id : 'place';
};

const findIconForType = (type: string | string[]) => {
  const group = placeTypeGroups
    .find(group => group.id === type || type.includes(group.id))
  return group != null ? group.icon : 'mdi-map-marker-radius';
};
